import { z } from 'zod';

import { boolFromQuery } from './_query';
import { ProjectSystemKind } from './projects.dto';
import { TaskListItemSchema } from './tasks.dto';

/**
 * My Work DTOs (docs/release-1/17-V2-BUILD-PLAN.md §4 W3-0).
 *
 * My Work is a PRIVATE overlay on top of team tasks: a plan date, an order, a
 * "my part done" mark, a private note and personal context. It lives in
 * self-only overlay tables (migration 0114: plans, private notes, personal
 * contexts) that nobody else can read —
 * not team owners, superadmin, agents, reports, webhooks or emails (§2.2).
 *
 * Shape rules:
 *  - Overlay data rides BESIDE the public task, never inside it (P3):
 *    `MyWorkItem.task` is the unchanged `TaskListItemSchema`.
 *  - Dates the user plans with are calendar dates in their own time zone
 *    (`LocalDate`, `YYYY-MM-DD`). The client sends its `today` so the server
 *    and the device agree on what "today" is.
 *  - Every route lives under `/api/me/*` and only ever answers about the
 *    caller. An invisible or foreign task is always `404 task_not_found`.
 */

/** A calendar date, `YYYY-MM-DD` (the type is `LocalDate` from `metrics`). */
export const LocalDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * Where a task sits in my plan:
 *  - `day`       a specific day (`planDate` = that day);
 *  - `week`      some time that week (`planDate` = the week start, per `users.week_start`);
 *  - `unplanned` in My Work, no date yet (`planDate` = null) — "Add to My Work";
 *  - `later`     parked on purpose (`planDate` = null).
 */
export const PlanScopeSchema = z.enum(['day', 'week', 'unplanned', 'later']);
export type PlanScope = z.infer<typeof PlanScopeSchema>;

/** Scopes that carry a date; the other two always have `planDate = null`. */
export const DATED_PLAN_SCOPES: readonly PlanScope[] = ['day', 'week'];

/** True iff `planDate` is null exactly when the scope is undated (DB CHECK mirror). */
function planDateMatchesScope(v: { scope: PlanScope; planDate: string | null }): boolean {
  return (v.scope === 'unplanned' || v.scope === 'later') === (v.planDate === null);
}

const PLAN_DATE_MESSAGE = 'planDate is required for day/week and must be null for unplanned/later';

/** Time block length bounds, minutes (W6; DB CHECK in 0119). */
export const PLAN_BLOCK_MINUTES_MIN = 15;
export const PLAN_BLOCK_MINUTES_MAX = 480;
const IsoDateTime = z.string().datetime({ offset: true });
const BlockMinutes = z.number().int().min(PLAN_BLOCK_MINUTES_MIN).max(PLAN_BLOCK_MINUTES_MAX);

export const TaskPlanSchema = z.object({
  taskId: z.string().uuid(),
  scope: PlanScopeSchema,
  /** day: that day; week: week start (users.week_start); unplanned | later: null. */
  planDate: LocalDateSchema.nullable(),
  /** Fractional index (base-62, `my-work/rank.ts`); order within the same scope + date. */
  rank: z.string(),
  /** Times the plan was moved from a past day to today or later ("Carried over"). */
  rolloverCount: z.number().int().min(0),
  /** Private "my part done" — never changes the team status. */
  doneAt: z.string().nullable(),
  /** Private reminder (W6, Personal tier): ISO instant; fires once as a push to me only. */
  remindAt: z.string().nullable(),
  /**
   * Private time block (W6, Personal tier; `scope='day'` only): ISO start
   * instant on `planDate` (my time zone) + length. Both null or both set.
   * Always sent by W6 servers; optional only so pre-W6 builders still compile.
   */
  blockStart: z.string().nullable().optional(),
  blockMinutes: BlockMinutes.nullable().optional(),
  updatedAt: z.string(),
});
export type TaskPlan = z.infer<typeof TaskPlanSchema>;

/**
 * Why a task is in My Work: I'm its assignee, its respondent, it's in my
 * Personal space, or I explicitly added it ("Add to My Work"). Tasks I only
 * authored for others are never auto-added (they show under role=created).
 */
export const MyWorkSourceSchema = z.enum(['assignee', 'respondent', 'personal', 'added']);
export type MyWorkSource = z.infer<typeof MyWorkSourceSchema>;

/**
 * What the ☐ checkbox does for this task, computed by the server from my
 * rights and the project's effective workflow (§1.4):
 *  - `status_done`   I may set Done → the real status moves to `targetStatusId`
 *                    (the first done-category state);
 *  - `status_review` I may change status and the workflow has a review state →
 *                    the status moves to it ("Sent for review") + private done;
 *  - `private_only`  otherwise (author, collaborator, Guest) → private done only;
 *                    the team status is unchanged.
 */
export const CompletionSchema = z.object({
  mode: z.enum(['status_done', 'status_review', 'private_only']),
  /** Legacy status key for `POST /api/tasks/:id/status`; null for `private_only`. */
  targetStatusId: z.string().nullable(),
});
export type MyWorkCompletion = z.infer<typeof CompletionSchema>;
export type MyWorkCompletionMode = MyWorkCompletion['mode'];

/**
 * The private context of one of MY personal tasks ("My steps" for WEB-45).
 * Labels are read live while I can see the target; nothing is snapshotted.
 */
export const PersonalContextSchema = z.object({
  kind: z.enum(['project', 'task', 'external', 'no_access', 'deleted']),
  projectId: z.string().uuid().nullable(),
  taskId: z.string().uuid().nullable(),
  /** Live "WEB · Website" / "WEB-45 Fix RTL"; null for no_access | deleted. */
  label: z.string().nullable(),
  externalUrl: z.string().nullable(),
});
export type PersonalContext = z.infer<typeof PersonalContextSchema>;
export type PersonalContextKind = PersonalContext['kind'];

export const MyWorkItemSchema = z.object({
  /** UNCHANGED public projection (P3). Overlay fields never go in here. */
  task: TaskListItemSchema,
  team: z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    kind: z.enum(['personal', 'regular']),
  }),
  project: z.object({
    id: z.string().uuid(),
    slug: z.string(),
    key: z.string(),
    name: z.string(),
    color: z.string().nullable(),
    systemKind: ProjectSystemKind.nullable(),
  }),
  status: z.object({
    id: z.string(),
    name: z.string(),
    color: z.string().nullable(),
    category: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
  }),
  source: MyWorkSourceSchema,
  plan: TaskPlanSchema.nullable(),
  /** Personal tasks only. */
  context: PersonalContextSchema.nullable(),
  hasNote: z.boolean(),
  /** My open personal tasks whose context_task_id = this task. */
  openStepsCount: z.number().int().min(0),
  completion: CompletionSchema,
  flags: z.object({
    /** The team deadline falls before my plan date. */
    deadlineBeforePlan: z.boolean(),
    /** I planned it, then it was reassigned to someone else (public fact). */
    reassignedAway: z.boolean(),
    /** Recently landed in My Work (e.g. newly assigned to me). */
    isNew: z.boolean(),
  }),
});
export type MyWorkItem = z.infer<typeof MyWorkItemSchema>;

// ── GET /api/me/work ────────────────────────────────────────────────────────

/**
 * `week` (W4): the Week board — see the section ids below.
 * `delegated` (W4): "Waiting on others" — Open tasks I authored and assigned
 * to someone else (`isDelegated`, §2.3). Public facts only; never auto-added
 * to my plan.
 * `done` (2026-10-19): "Completed" — Mine tasks I finished (team Done, or my
 * private "done in my plan") in the last `doneDays` days, newest first.
 */
export const MyWorkViewSchema = z.enum([
  'today',
  'unplanned',
  'later',
  'all',
  'week',
  'delegated',
  'done',
]);
export type MyWorkView = z.infer<typeof MyWorkViewSchema>;

/** `view=all` role chips: Mine · Created by me · Collaborating. */
export const MyWorkAllRoleSchema = z.enum(['mine', 'created', 'collaborating']);
export type MyWorkAllRole = z.infer<typeof MyWorkAllRoleSchema>;

export const MyWorkQuerySchema = z
  .object({
    view: MyWorkViewSchema,
    today: LocalDateSchema,
    /** view=all only. */
    role: MyWorkAllRoleSchema.default('mine'),
    /** view=all only. Parsed strictly: `?includeDone=false` is false (not `Boolean('false')`). */
    includeDone: boolFromQuery().default(false),
    /**
     * view=week only: any date inside the wanted week. The server snaps it to
     * the week start (`users.week_start`); omitted = the week of `today`.
     */
    weekOf: LocalDateSchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    /** view=done only: how many days back "Completed" reaches (default 7). */
    doneDays: z.coerce.number().int().min(1).max(30).optional(),
  })
  .strict();
export type MyWorkQuery = z.infer<typeof MyWorkQuerySchema>;
export type MyWorkQueryInput = z.input<typeof MyWorkQuerySchema>;

/**
 * Section ids. Views and the sections they return, in this order:
 *  - today      carried_over · overdue · due_today · today · suggested (P1 only) ·
 *               upcoming · done_today — overdue / due_today: unplaced Mine rows
 *  - unplanned  unplanned        - later  later        - all  all (cursor-paged)
 *  - week       week_day ×7 (one per day, week order) · week_nodate ·
 *               due_unplanned · unplanned · later
 *  - delegated  delegated
 *  - done       done_recent — Mine ∪ planned tasks done in the team
 *               (`completedAt`) or privately (`plan.doneAt`) within
 *               `doneDays`, newest first, at most `limit` (not paged)
 *
 * Week sections (W4):
 *  - `week_day`      `date` = that day. My plans with `scope='day'` on that
 *                    date, by rank; archived and cancelled tasks excluded,
 *                    done ones kept (privately or in the team — shown ticked)
 *                    so a past day keeps its record.
 *  - `week_nodate`   `date` = the week start. `scope='week'` plans for this
 *                    week ("This week, no day"), same inclusion rule, by rank.
 *  - `due_unplanned` Mine (§2.3, never author-only) ∧ Open ∧ not privately
 *                    done ∧ deadline's local date (my time zone) inside the
 *                    week ∧ no day / week plan inside this week. By deadline,
 *                    then priority. The board shows them as ghost chips in the
 *                    column of their deadline date and in the left rail "Due
 *                    this week (not planned)". An item may also be in `unplanned`.
 *  - `unplanned`, `later` the same rules as their own views (left rail).
 * `delegated`: `isDelegated` (Open ∧ author = me ∧ assignee ∉ {me, null}),
 * ordered by assignee name, then deadline (nulls last); grouped by person
 * client-side with `MyWorkResponse.people`. Not paged (`nextCursor` null).
 */
export const MyWorkSectionIdSchema = z.enum([
  'carried_over',
  'today',
  'suggested',
  'overdue',
  'due_today',
  'upcoming',
  'done_today',
  'unplanned',
  'later',
  'all',
  'week_day',
  'week_nodate',
  'due_unplanned',
  'delegated',
  'done_recent',
]);
export type MyWorkSectionId = z.infer<typeof MyWorkSectionIdSchema>;

export const MyWorkSectionSchema = z.object({
  id: MyWorkSectionIdSchema,
  items: z.array(MyWorkItemSchema),
  nextCursor: z.string().nullable(),
  /** `week_day`: that day; `week_nodate`: the week start; null for every other section. */
  date: LocalDateSchema.nullable().default(null),
  /**
   * `week_day` / `week_nodate`: Σ round(task.laborHours × 60) over `items`
   * (unestimated = 0) — the client recomputes it the same way after a move.
   * Private (it's my plan); null for every other section.
   */
  plannedMinutes: z.number().int().nullable().default(null),
  /** `week_day`: my daily capacity (work prefs `dailyCapacityMinutes`); null elsewhere. */
  capacityMinutes: z.number().int().nullable().default(null),
});
export type MyWorkSection = z.infer<typeof MyWorkSectionSchema>;

/**
 * A milestone on the Week board header / Calendar "Milestones" layer and in
 * Home › My week: every milestone of my teams **plus Personal** whose planned
 * end falls in the range. Public facts only (`atRisk` = `isMilestoneAtRisk`).
 */
export const WeekMilestoneSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  teamId: z.string().uuid(),
  /** Raw team name; show `personalTeamLabel` when `isPersonal`. */
  teamName: z.string(),
  teamSlug: z.string(),
  projectId: z.string().uuid(),
  projectSlug: z.string(),
  projectName: z.string(),
  plannedEndDate: LocalDateSchema,
  atRisk: z.boolean(),
  isPersonal: z.boolean(),
});
export type WeekMilestone = z.infer<typeof WeekMilestoneSchema>;

/** Someone a delegated task waits on (view=delegated) — public profile fields only. */
export const MyWorkPersonSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
});
export type MyWorkPerson = z.infer<typeof MyWorkPersonSchema>;

/** Badge numbers (mobile tab, sidebar). Mine only; never derived for anyone else. */
export const MyWorkCountsSchema = z.object({
  /** All visible My Work candidates, across every plan date. Optional for older servers. */
  capturedTotal: z.number().int().nonnegative().optional(),
  todayRemaining: z.number().int(),
  todayDone: z.number().int(),
  carriedOver: z.number().int(),
  unplanned: z.number().int(),
  later: z.number().int(),
  /** Today › Overdue / Due today (2026-10-19). Optional for older servers. */
  overdue: z.number().int().nonnegative().optional(),
  dueToday: z.number().int().nonnegative().optional(),
});
export type MyWorkCounts = z.infer<typeof MyWorkCountsSchema>;

export const MyWorkResponseSchema = z.object({
  view: MyWorkViewSchema,
  today: LocalDateSchema,
  sections: z.array(MyWorkSectionSchema),
  counts: MyWorkCountsSchema,
  /** view=week: milestones ending inside the week (all my teams + Personal); [] otherwise. */
  milestones: z.array(WeekMilestoneSchema).default([]),
  /** view=delegated: the assignees of the listed tasks, for "Waiting on others" by person; [] otherwise. */
  people: z.array(MyWorkPersonSchema).default([]),
});
export type MyWorkResponse = z.infer<typeof MyWorkResponseSchema>;

/** Most weeks one `GET /api/me/work/weeks` returns — a month grid is ≤ 6 weeks. */
export const MY_WORK_WEEKS_MAX = 6;

/**
 * `GET /api/me/work/weeks?today=&from=&weeks=` — `weeks` consecutive
 * `view=week` responses in ONE request (the Calendar's month grid), starting
 * with the week containing `from`.  Each element is exactly what
 * `GET /api/me/work?view=week&weekOf=<that week>` answers, so the client files
 * them under the same per-week cache keys.
 */
export const MyWorkWeeksQuerySchema = z
  .object({
    today: LocalDateSchema,
    from: LocalDateSchema,
    weeks: z.coerce.number().int().min(1).max(MY_WORK_WEEKS_MAX),
  })
  .strict();
export type MyWorkWeeksQuery = z.infer<typeof MyWorkWeeksQuerySchema>;

export const MyWorkWeeksResponseSchema = z.object({
  weeks: z.array(MyWorkResponseSchema).max(MY_WORK_WEEKS_MAX),
});
export type MyWorkWeeksResponse = z.infer<typeof MyWorkWeeksResponseSchema>;

// ── Plan mutations ──────────────────────────────────────────────────────────

/**
 * PUT /api/me/plan/:taskId — plan, move, reorder, or "Add to My Work"
 * (`scope:'unplanned'`). `after` / `before` are the neighbours in the target
 * list (the item directly above / directly below); pass either or both.
 * Without them the task goes to the end of the list.
 *
 * W6 (Personal tier — a non-null value without entitlement → 402
 * `upgrade_required`; `null` always clears): omitted = unchanged, `null` =
 * clear. `blockStart` + `blockMinutes` travel together and need
 * `scope='day'` with the block's local date = `planDate`; a move to another
 * day / scope that omits them drops the block. A moved reminder re-arms.
 */
export const PutPlanSchema = z
  .object({
    scope: PlanScopeSchema,
    planDate: LocalDateSchema.nullable(),
    before: z.string().uuid().nullable().optional(),
    after: z.string().uuid().nullable().optional(),
    remindAt: IsoDateTime.nullable().optional(),
    blockStart: IsoDateTime.nullable().optional(),
    blockMinutes: BlockMinutes.nullable().optional(),
    today: LocalDateSchema,
  })
  .strict()
  .refine(planDateMatchesScope, { message: PLAN_DATE_MESSAGE, path: ['planDate'] })
  .refine(
    (v) =>
      (v.blockStart === undefined) === (v.blockMinutes === undefined) &&
      (v.blockStart === null) === (v.blockMinutes === null) &&
      (v.blockStart == null || v.scope === 'day'),
    { message: 'blockStart and blockMinutes go together, on a day plan only', path: ['blockStart'] },
  );
export type PutPlanInput = z.infer<typeof PutPlanSchema>;

/** POST /api/me/plan/bulk — e.g. "Move all to today". Invisible ids are skipped, never 404. */
export const BulkPlanSchema = z
  .object({
    taskIds: z.array(z.string().uuid()).min(1).max(100),
    scope: PlanScopeSchema,
    planDate: LocalDateSchema.nullable(),
    today: LocalDateSchema,
  })
  .strict()
  .refine(planDateMatchesScope, { message: PLAN_DATE_MESSAGE, path: ['planDate'] });
export type BulkPlanInput = z.infer<typeof BulkPlanSchema>;

export const BulkPlanResultSchema = z.object({
  plans: z.array(TaskPlanSchema),
  skipped: z.array(z.string().uuid()),
});
export type BulkPlanResult = z.infer<typeof BulkPlanResultSchema>;

/** POST /api/me/plan/:taskId/done — private "my part done" (creates a day plan for `today` if missing). */
export const MarkPlanDoneSchema = z.object({ done: z.boolean(), today: LocalDateSchema }).strict();
export type MarkPlanDoneInput = z.infer<typeof MarkPlanDoneSchema>;

// ── Capture ─────────────────────────────────────────────────────────────────

/**
 * POST /api/me/capture — the client parses tokens (`завтра #WEB !1 ^пт`);
 * the server parses nothing. Default target: my Personal inbox (private).
 * `forTeamProjectId` is the explicit public act: a real team task assigned to
 * me (G2). `@mentions` are never sent (they'd notify from a private capture).
 */
export const CaptureSchema = z
  .object({
    title: z.string().trim().min(1).max(500),
    /** Plain text → one TipTap paragraph server-side. */
    description: z.string().max(10000).optional(),
    scope: PlanScopeSchema.optional(),
    planDate: LocalDateSchema.nullable().optional(),
    priority: z.number().int().min(1).max(4).optional(),
    /** Team-visible deadline (date-only, end of day in the project's time zone). */
    deadline: LocalDateSchema.nullable().optional(),
    /** Private context of the new personal task (`#WEB` from My Work). */
    context: z
      .object({ projectId: z.string().uuid().optional(), taskId: z.string().uuid().optional() })
      .optional(),
    /** Explicit public act: real team task, assignee = me. */
    forTeamProjectId: z.string().uuid().optional(),
    today: LocalDateSchema,
  })
  .strict()
  .refine((v) => !(v.context && v.forTeamProjectId), {
    message: 'context and forTeamProjectId are mutually exclusive',
    path: ['forTeamProjectId'],
  })
  .refine(
    (v) =>
      v.scope === undefined
        ? v.planDate === undefined || v.planDate === null
        : planDateMatchesScope({ scope: v.scope, planDate: v.planDate ?? null }),
    { message: PLAN_DATE_MESSAGE, path: ['planDate'] },
  );
export type CaptureInput = z.infer<typeof CaptureSchema>;

// ── The private panel in a task ("🔒 Only you can see this") ────────────────

export const PrivateNoteSchema = z.object({ bodyText: z.string(), updatedAt: z.string() });
export type PrivateNote = z.infer<typeof PrivateNoteSchema>;

/** GET /api/me/tasks/:taskId/mine — query key ['me','mine',taskId]; never part of TaskProjection. */
export const MinePanelSchema = z.object({
  taskId: z.string().uuid(),
  inMyWork: z.boolean(),
  source: MyWorkSourceSchema.nullable(),
  plan: TaskPlanSchema.nullable(),
  note: PrivateNoteSchema.nullable(),
  /** My personal tasks with context_task_id = taskId ("My steps"). */
  steps: z.array(MyWorkItemSchema),
  /**
   * What MY checkbox does on this task (same rule as `MyWorkItem.completion`,
   * §1.4) — drives "Done in my plan" when I can't change the team status.
   * Always sent by the server; optional only so older fixtures still parse.
   */
  completion: CompletionSchema.optional(),
});
export type MinePanel = z.infer<typeof MinePanelSchema>;

/** PUT /api/me/tasks/:taskId/note — text only (no mentions, no files). */
export const PutPrivateNoteSchema = z.object({ bodyText: z.string().max(20000) }).strict();
export type PutPrivateNoteInput = z.infer<typeof PutPrivateNoteSchema>;

/** PUT /api/me/tasks/:taskId/context — `:taskId` must be MY personal task. */
export const PutPersonalContextSchema = z
  .object({
    contextProjectId: z.string().uuid().nullable(),
    contextTaskId: z.string().uuid().nullable(),
    externalUrl: z.string().url().max(2000).nullable().optional(),
    externalLabel: z.string().max(120).nullable().optional(),
  })
  .strict();
export type PutPersonalContextInput = z.infer<typeof PutPersonalContextSchema>;

/**
 * POST /api/me/tasks/:taskId/send-to-team (W6-E; Idempotency-Key), the explicit public act:
 * `:taskId` = MY personal task (else 404 `task_not_found`). Copies title, description,
 * priority (+ my note only if `includeNotes`) into `projectId`, closes the personal task,
 * links back via my private personal context (never `task_relations`); no "from personal" trace.
 */
export const SendToTeamSchema = z
  .object({ projectId: z.string().uuid(), includeNotes: z.boolean().default(false) })
  .strict();
export type SendToTeamInput = z.infer<typeof SendToTeamSchema>;
export const SendToTeamResultSchema = z.object({
  task: TaskListItemSchema,
  /** The team copy exists even when closing the Personal source is governed. */
  sourceClose: z.enum(['policy_denied', 'approval_pending']).optional(),
});
export type SendToTeamResult = z.infer<typeof SendToTeamResultSchema>;

// ── Realtime + PAT scopes ───────────────────────────────────────────────────

/**
 * SSE frames about my own plan. Sent ONLY via `realtime.pushTo([me], …)` and
 * never carry a `teamId` (P7) — webhooks drop teamId-less payloads.
 */
export const MeRealtimeEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('me.plan.changed'),
    payload: z.object({ taskIds: z.array(z.string().uuid()) }),
  }),
  z.object({
    type: z.literal('me.mine.changed'),
    payload: z.object({ taskId: z.string().uuid() }),
  }),
]);
export type MeRealtimeEvent = z.infer<typeof MeRealtimeEventSchema>;

/** PAT scopes that unlock My Work routes (P6). Agent PATs (`ai.agent_act`) are always rejected. */
export const ME_PLAN_SCOPES = ['me.plan:read', 'me.plan:write'] as const;
export type MePlanScope = (typeof ME_PLAN_SCOPES)[number];
